import React from "react"
import { CircularProgress, Link, Tooltip } from "@mui/material"
import { UploadSharp } from "@mui/icons-material"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import {
  UserRole,
  allowedOnlyBankAccountsRoles,
  processScheduleStatus,
} from "../../constants/data"
import { selectLogin } from "../../slices/loginSlice"
import { selectTransactions } from "../../slices/transactionsSlice"
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined"
import {
  HeaderActionStack,
  PrimaryButton,
  ReconciliationMessage,
  ReconciliationMessageText,
  StyledButton,
  StyledCancelReconcileButton,
} from "./styles"
import { TRANSACTIONS_UPLOAD_HISTORY } from "../../constants/path"
import { NavLink } from "react-router-dom"
import { showToast } from "../../slices/toastSlice"

interface PageHeaderActionProps {
  setUploadDialogOpen: (value: React.SetStateAction<boolean>) => void
  setScheduleDialogOpen: (value: React.SetStateAction<boolean>) => void
}

export const PageHeaderAction: React.FC<PageHeaderActionProps> = ({
  setUploadDialogOpen,
  setScheduleDialogOpen,
}) => {
  const dispatch = useAppDispatch()
  const { role } = useAppSelector(selectLogin)

  const { isUploading, processScheduleData, isProcessScheduleLoading } =
    useAppSelector(selectTransactions)

  const handleUploadDialogOpen = () => {
    setUploadDialogOpen(true)
  }

  const handleProcessSchedule = () => {
    processScheduleData.scheduleStatus !== processScheduleStatus.IN_PROGRESS
      ? setScheduleDialogOpen(true)
      : dispatch(
          showToast({
            toastType: "info",
            msg: "Automation is currently reconciling your data  - please try again later",
          }),
        )
  }

  return (
    <HeaderActionStack>
      <PrimaryButton
        variant="contained"
        startIcon={<UploadSharp fontSize="small" />}
        onClick={handleUploadDialogOpen}
        disabled={
          isUploading ||
          role === UserRole.ACCOUNT ||
          role === UserRole.ALTS_SALES
        }
      >
        Upload Bank Statement
      </PrimaryButton>
      {processScheduleData.status &&
      allowedOnlyBankAccountsRoles.includes(role) ? (
        <StyledCancelReconcileButton
          variant="outlined"
          startIcon={<CalendarTodayOutlinedIcon fontSize="small" />}
          disabled={isProcessScheduleLoading}
          onClick={handleProcessSchedule}
          scheduleStatus={processScheduleData.scheduleStatus}
        >
          {isProcessScheduleLoading ? (
            <CircularProgress size={18} sx={{ mx: "18px" }} color="primary" />
          ) : processScheduleData.scheduleStatus ===
            processScheduleStatus.PENDING ? (
            processScheduleData?.nextRun
          ) : (
            "Reconcilation in progress"
          )}
        </StyledCancelReconcileButton>
      ) : allowedOnlyBankAccountsRoles.includes(role) ? (
        <Tooltip
          title={
            processScheduleData.status
              ? processScheduleData?.message
              : "Click to process schedule"
          }
        >
          <StyledButton
            variant="outlined"
            startIcon={<CalendarTodayOutlinedIcon fontSize="small" />}
            color="inherit"
            disabled={isProcessScheduleLoading}
            onClick={handleProcessSchedule}
          >
            {isProcessScheduleLoading ? (
              <CircularProgress size={18} sx={{ mx: "18px" }} color="primary" />
            ) : (
              "Schedule"
            )}
          </StyledButton>
        </Tooltip>
      ) : (
        <ReconciliationMessage>
          <ReconciliationMessageText>
            Reconciliation not scheduled
          </ReconciliationMessageText>
        </ReconciliationMessage>
      )}
      <Link
        component={NavLink}
        to={TRANSACTIONS_UPLOAD_HISTORY}
        underline="hover"
        color="primary"
      >
        <StyledButton variant="outlined" color="inherit" sx={{ width: "100%" }}>
          Upload History
        </StyledButton>
      </Link>
    </HeaderActionStack>
  )
}
